/**
 * Collect every Builder block (component) name that a content entry can
 * actually RENDER.
 *
 * Walks the whole entry tree — `data.blocks`, element `children`, and nested
 * element arrays inside `component.options` (Columns' `columns[].blocks`,
 * accordion `triggerBlocks`/`panelBlocks`, inlined Symbol content under
 * `component.options.symbol.content`, …) — collecting `component.name`
 * wherever a component object appears.
 *
 * `@builder.io/core:Reference` values are SKIPPED: with `includeRefs=true`
 * Builder inlines each referenced entry's full `value.data` (e.g. a nav
 * link's target page, ~100KB of that page's blocks), but references are
 * consumed as DATA (url/title) — their block trees never render on the
 * referencing surface. Counting them would drag every block of every linked
 * page into the audit (the FSA aux nav "references" a ProductCarousel this
 * way). Mirrors `stripReferencedPageBlocks` (~/hec/utils/builder-trim-references),
 * which deletes exactly those subtrees before the chrome SWR cache write.
 *
 * Shared by the chrome-registry runtime guard (chrome-content.tsx) and the
 * chrome fixture audit test (tests/unit/hec/builder/chrome-registry.test.ts)
 * so the two cannot drift.
 */
const REFERENCE_TYPE = "@builder.io/core:Reference";

export function collectRenderedBlockNames(
  node: unknown,
  out: Set<string> = new Set(),
): Set<string> {
  if (Array.isArray(node)) {
    for (const item of node) collectRenderedBlockNames(item, out);
    return out;
  }
  if (!node || typeof node !== "object") return out;
  const obj = node as Record<string, unknown>;

  // Reference values are data, never rendered blocks — do not descend.
  if (obj["@type"] === REFERENCE_TYPE) return out;

  const component = obj.component as { name?: unknown } | undefined;
  if (component && typeof component.name === "string") {
    out.add(component.name);
  }

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      collectRenderedBlockNames(obj[key], out);
    }
  }
  return out;
}

/**
 * `component.options` of the first rendered block named `name`, or null. Same
 * traversal as collectRenderedBlockNames (reference values skipped).
 */
export function findRenderedBlockOptions(
  node: unknown,
  name: string,
): Record<string, unknown> | null {
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findRenderedBlockOptions(item, name);
      if (found) return found;
    }
    return null;
  }
  if (!node || typeof node !== "object") return null;
  const obj = node as Record<string, unknown>;
  if (obj["@type"] === REFERENCE_TYPE) return null;

  const component = obj.component as
    | { name?: unknown; options?: Record<string, unknown> }
    | undefined;
  if (component?.name === name) return component.options ?? {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const found = findRenderedBlockOptions(obj[key], name);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Collect every named icon an entry's IconPicker blocks can render — same
 * traversal rules as collectRenderedBlockNames (skip reference values, walk
 * children/options/variations). `useReference` icons render an `<img>` from
 * the referenced entry and need no map, so they are excluded.
 *
 * Shared by the chrome icon-map fixture test
 * (tests/unit/hec/builder/chrome-registry.test.ts), which pins the audited
 * chrome icon subset (icon-picker/chrome.tsx) against the real chrome
 * entries (COM-188).
 */
export function collectRenderedIconNames(
  node: unknown,
  out: Set<string> = new Set(),
): Set<string> {
  if (Array.isArray(node)) {
    for (const item of node) collectRenderedIconNames(item, out);
    return out;
  }
  if (!node || typeof node !== "object") return out;
  const obj = node as Record<string, unknown>;

  // Reference values are data, never rendered blocks — do not descend.
  if (obj["@type"] === REFERENCE_TYPE) return out;

  const component = obj.component as
    | {
        name?: unknown;
        options?: { iconName?: unknown; useReference?: unknown };
      }
    | undefined;
  if (
    component?.name === "IconPicker" &&
    !component.options?.useReference &&
    typeof component.options?.iconName === "string"
  ) {
    out.add(component.options.iconName);
  }

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      collectRenderedIconNames(obj[key], out);
    }
  }
  return out;
}
